import { useMemo, memo, useRef, useEffect } from "react";
import { Box, Button, MenuItem, MenuList } from "@mui/material";
import { useGridApiContext, useGridSelector, gridPaginationModelSelector, gridFilteredTopLevelRowCountSelector, useGridRootProps } from "@mui/x-data-grid-pro";

import { Text } from "@imminently/react/ui/core/text";
import { Menu } from "@imminently/react/ui/menu/menu";

import Pagination from "@/components/Pagination";
import { Icon } from "@/components/Icon";
import { css, Theme } from "@/theme";

const styles = {
  pagination: (theme: Theme) => css`
    background-color: ${theme.palette.common.white};
    border-top: 1px solid ${theme.palette.primary[100]};
    flex: 1;
    align-items: center;
    padding: 0.25rem 1rem;
    padding-left: 0.75rem;
  `,
};

const PageSizeSelector = () => {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);

  const setPageSize = (size: number) => {
    apiRef.current.setPageSize(size);
  };

  const button = (props: any) => (
    <Button {...props} variant="text" sx={{ p: 0, gap: 0.5 }}>
      <Text variant="body1">Results per page:</Text>
      <Text variant="button">{paginationModel.pageSize}</Text>
      <Icon icon="chevrondown" />
    </Button>
  );

  return (
    <Menu menuComponent={button}>
      <MenuList sx={{ width: "70px" }}>
        {(rootProps as any).pageSizeOptions.map((option: any) => {
          const value = typeof option === "number" ? option : option.value;
          const label = typeof option === "number" ? option : option.label;

          return (
            <MenuItem key={value} value={value} onClick={() => setPageSize(value)}>
              {label}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};

export const PatientPagination = memo(() => {
  // NOTE: This component might still re-render on horizontal scroll of the parent DataGridPro.
  // This is likely because DataGridPro re-renders when its parent (PatientListCore) updates due to scrollLeft changes.
  // If DataGridPro's internal context objects (consumed via useGridApiContext/useGridRootProps)
  // are new instances on each DataGridPro render, this component will re-render even if the actual
  // pagination data (page, pageSize, rowCount) has not changed. This was confirmed by adding logs
  // to track changes in paginationModel and rowCount, which showed no changes during horizontal scroll.
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const visibleTopLevelRowCount = useGridSelector(apiRef, gridFilteredTopLevelRowCountSelector);

  const prevPaginationModelRef = useRef(paginationModel);
  const prevRowCountRef = useRef(rootProps.rowCount);

  useEffect(() => {
    if (paginationModel.page !== prevPaginationModelRef.current.page || paginationModel.pageSize !== prevPaginationModelRef.current.pageSize) {
      // console.log("[PatientPagination] paginationModel CHANGED from:", prevPaginationModelRef.current, "to:", paginationModel);
      prevPaginationModelRef.current = paginationModel;
    }
    if (rootProps.rowCount !== prevRowCountRef.current) {
      // console.log("[PatientPagination] rootProps.rowCount CHANGED from:", prevRowCountRef.current, "to:", rootProps.rowCount);
      prevRowCountRef.current = rootProps.rowCount;
    }
  });

  // Original logs (can be commented out if too noisy after adding change detection)
  console.log("[PatientPagination] Rendering. rootProps available:", !!rootProps, "apiRef.current available:", !!apiRef.current);
  if (apiRef.current) {
    // console.log("[PatientPagination] paginationModel from grid:", paginationModel); // Covered by change detection log
    // console.log("[PatientPagination] rootProps.rowCount:", rootProps.rowCount); // Covered by change detection log
  }

  const rowCount = useMemo(
    () => rootProps.rowCount ?? visibleTopLevelRowCount ?? 0,
    [rootProps.rowCount, visibleTopLevelRowCount],
  );

  const state = {
    from: paginationModel.page * paginationModel.pageSize + 1,
    to: Math.min((paginationModel.page + 1) * paginationModel.pageSize, rowCount),
    count: rowCount,
    page: paginationModel.page + 1,
  };

  // console.log("page data", { paginationModel, pageCount, rowCount, state });

  const setPage = (_: unknown, page: number) => {
    apiRef.current.setPage(page - 1);
  };

  // TODO for some reason pageCount is not working
  // using paginationModel.pageSize and rowCount, determine number of pages
  const customCount = Math.ceil(rowCount / paginationModel.pageSize);

  // console.log("[PatientPagination]", { paginationModel, pageCount, rowCount, state, customCount });

  return (
    <Pagination css={styles.pagination} >
      <Pagination.Actions showFirstButton showLastButton color="primary" count={customCount} page={paginationModel.page + 1} onChange={setPage} />
      <Box flex={1} />
      <Box display="flex" gap={8} alignItems="center">
        <Pagination.DisplayedRows {...state} />
        {/* <Pagination.RowsPerPage size="small" value={paginationModel.pageSize} label="Results per page:" options={rootProps.pageSizeOptions} /> */}
        <PageSizeSelector />
      </Box>
    </Pagination>
  );
});

PatientPagination.displayName = "PatientPagination";

export default PatientPagination;